// Seed prompt for "Investigate with Cade" from a single crypto-inventory row.
// Spec: docs/spec/topics/pqc/pqc-asset-discovery.md §7.3 (bulk-investigate
// seed). This is the single-row variant of the §7.3 template — same four
// labeled lines the row drawer shows, plus the ground-truth framing so Cade
// treats the row as authoritative rather than re-deriving it.
//
// Kept as a plain module (not JSX) so a vitest can pin the exact rendered
// text — the seed IS the tool contract with the model.

export interface InvestigateRow {
  name: string;
  sourceLabel: string;
  platform: string;
  platformAccount: string;
  materialKindLabel: string;
  privateKeyHolderLabel: string;
  rotationLabel: string;
  functionSummary: string;
  algorithm: string;
  priorityBand: string | null;
  priorityScore: number | null;
  threatScenario: string;
  isTokenSigning: boolean;
  notAfter: string | null;
  status: string;
  managedBy: 'customer' | 'platform';
  keyNodeId?: string | null;
}

function line(label: string, value: string): string {
  return `- ${label}: ${value}`;
}

/**
 * Deterministic seed text — no randomness, no timestamps, no free-form
 * substitution. Every field is passed through verbatim; the caller supplies
 * pre-labeled values (e.g. "Microsoft (Entra holds the private key)" rather
 * than `microsoft`) so the model sees analyst-facing text, not enum values.
 */
export function buildInvestigateSeed(row: InvestigateRow): string {
  const priority = row.priorityBand
    ? `${row.priorityBand}${row.priorityScore != null ? ` (score ${row.priorityScore})` : ''}`
    : 'unscored';
  const scenarioLine = `${row.threatScenario}${row.isTokenSigning ? ' — token-signing (Storm-0558 class)' : ''}`;
  const graphAnchor = row.keyNodeId
    ? `${row.keyNodeId} — call \`crypto_list_assets\` with a filter that returns this row if you need the linked owner node id.`
    : 'not yet linked into the graph (a discovery run has not populated it, or the row failed the thumbprint identity gate)';

  const remediationRule = row.managedBy === 'platform'
    ? 'The row is managed_by=platform. State plainly that the customer cannot rotate this material — the vendor owns the PQC migration. Do NOT propose a rotation the customer cannot execute.'
    : 'The row is customer-managed. Name the concrete PQC-migration action (rotate, replace, defer) with the specific replacement algorithm/size if applicable.';

  return [
    'Investigate this cryptographic asset from the tenant\'s post-quantum inventory. Treat the fields below as ground truth — they come from the inventory table, not from your own reasoning.',
    '',
    line('Name', row.name),
    line('Source', `${row.sourceLabel} · ${row.platform} · ${row.platformAccount}`),
    line('What this is', row.materialKindLabel),
    line('What it serves', row.functionSummary || '—'),
    line('Who holds the private key', row.privateKeyHolderLabel),
    line('Who rotates it', row.rotationLabel),
    line('Algorithm', row.algorithm),
    line('Priority', priority),
    line('Threat scenario', scenarioLine),
    line('Expires', row.notAfter ?? 'no expiry'),
    line('Status', row.status),
    line('Graph node', graphAnchor),
    '',
    'Your job, in this order:',
    `1. ${remediationRule}`,
    '2. If the row is quantum-vulnerable and token-signing, treat it as the Storm-0558 class and prioritise accordingly.',
    '3. Do NOT propose enrichment or new discovery runs — the inventory is already the read-only source of truth. If you want more detail on this asset or its cluster, call `crypto_list_assets` to filter for related rows (same source_service, same platform_account, same threat_scenario).',
  ].join('\n');
}
